import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { dateFormat } from '@bloomays-lib/utils.shared';
import { ApolloGqlTalentForTalentBoard, TalentBoardEventType } from '@bloomays-lib/types.shared';
import {
  Button,
  TalentExperiencesSection,
  TalentFormationsSection,
  TalentLanguagesSection,
} from '@bloomays-lib/ui.shared';

import { Logger } from '../../services/service-logger';

const logger = Logger('TalentProfile');

type TalentProfileProps = {
  jobId: string;
  jobName: string;
  talent: ApolloGqlTalentForTalentBoard;
  contentTab: 'experience' | 'formation';
  setContentTab: (contentTab: 'experience' | 'formation') => void;
};

const TalentProfile = ({ jobId, jobName, talent, contentTab, setContentTab }: TalentProfileProps) => {
  const { t } = useTranslation('talentBoard');

  return (
    <>
      <ContentTabButtonWrapper>
        <Button
          textButton={t('talentJob.experience')}
          onClick={() => {
            logger.info('Traffic', {
              page: 'TalentProfile',
              evt: { name: TalentBoardEventType.TalentboardOpenExperienceProfile },
              job: {
                id: jobId,
                name: jobName,
              },
              talent: {
                id: talent.id,
                name: talent.firstname + ' ' + talent.lastname,
              },
            });
            setContentTab('experience');
          }}
          sx={{ background: contentTab === 'experience' ? '' : 'transparent' }}
        />
        <Button
          textButton={t('talentJob.formation')}
          onClick={() => {
            logger.info('Traffic', {
              page: 'TalentProfile',
              evt: { name: TalentBoardEventType.TalentboardOpenFormationProfile },
              job: {
                id: jobId,
                name: jobName,
              },
              talent: {
                id: talent.id,
                name: talent.firstname + ' ' + talent.lastname,
              },
            });
            setContentTab('formation');
          }}
          sx={{ background: contentTab === 'formation' ? '' : 'transparent' }}
        />
      </ContentTabButtonWrapper>
      <ContentContainer>
        {contentTab === 'experience' && (
          <TalentExperiencesSection
            jobs={
              talent.jobs?.map((job) => ({
                id: job.id ?? '',
                jobTitle: job.jobTitle?.map((jt) => ({
                  id: jt.id,
                  label: jt.label,
                  iso: jt.iso ?? undefined,
                })),
                period: job.period
                  ? {
                      id: job.period.id,
                      startDate: dateFormat(job.period.startDate, 'yyyy-MM-dd'),
                      endDate: job.period.endDate ? dateFormat(job.period.endDate, 'yyyy-MM-dd') : undefined,
                    }
                  : undefined,
                current: job.current ?? undefined,
                label: job.label ?? undefined,
                description: job.description ?? undefined,
                name: job.name ?? undefined,
                skills: job.skills?.map((skill) => ({
                  id: skill.id,
                  label: skill.label,
                  iso: skill.iso,
                  alternateLabels: skill.alternateLabels,
                })),
              })) || []
            }
          />
        )}
        {contentTab === 'formation' && (
          <>
            <TalentFormationsSection
              educations={
                talent.educations?.map((education) => ({
                  id: education.id ?? '',
                  name: education.name,
                  label: education.label ?? undefined,
                  description: education.description ?? undefined,
                  current: education.current ?? undefined,
                  url: education.url ?? undefined,
                  type: education.type ?? undefined,
                  period: education.period
                    ? {
                        id: education.period.id,
                        startDate: dateFormat(education.period.startDate, 'yyyy-MM-dd'),
                        endDate: education.period.endDate
                          ? dateFormat(education.period.endDate, 'yyyy-MM-dd')
                          : undefined,
                      }
                    : undefined,
                })) || []
              }
            />
            <TalentLanguagesSection
              languages={
                talent.languages?.map((language) => ({
                  id: language.id ?? '',
                  label: language.label,
                  level: language.level ?? undefined,
                })) || []
              }
            />
          </>
        )}
      </ContentContainer>
    </>
  );
};

const ContentTabButtonWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: '16px',
  [theme.breakpoints.down('md')]: {
    gap: '8px',
    paddingBottom: '24px',
    backgroundColor: theme.palette.neutral[10],
    justifyContent: 'center',
  },
}));

const ContentContainer = styled('div')(({ theme }) => ({
  padding: '24px 0px',
  [theme.breakpoints.down('md')]: {
    padding: '16px 0px',
  },
}));

export { TalentProfile };
export default TalentProfile;
